import { AlertOctagon, ArchiveX, Clock3, DatabaseZap, FileClock, ShieldAlert, UserRoundX } from 'lucide-react';
import type { AuditEventDto } from '../types';
import { formatDuration, formatTime } from '../lib/format';

interface AuditPanelProps {
  items: readonly AuditEventDto[];
  loading: boolean;
  error: { code?: string; message: string } | undefined;
  onRetry: () => void;
}

const outcomeLabel: Record<AuditEventDto['outcome'], string> = {
  succeeded: '成功',
  rejected: '拒绝',
  failed: '失败',
};

export function AuditPanel({ items, loading, error, onRetry }: AuditPanelProps) {
  if (loading) {
    return (
      <section className="audit-panel panel" aria-label="正在加载审计记录">
        <div className="audit-skeleton"><span /><span /><span /><span /></div>
      </section>
    );
  }

  if (error) {
    const unconfigured = error.code === 'AUDIT_STORE_UNAVAILABLE';
    return (
      <section className="audit-panel panel audit-state">
        <div className={`audit-state__icon${unconfigured ? ' audit-state__icon--warning' : ''}`}>
          {unconfigured ? <DatabaseZap size={27} /> : <AlertOctagon size={27} />}
        </div>
        <span className="eyebrow">{unconfigured ? 'AUDIT STORE NOT CONFIGURED' : 'AUDIT API ERROR'}</span>
        <h2>{unconfigured ? '审计存储尚未配置' : '审计记录加载失败'}</h2>
        <p>{unconfigured ? 'GM API 正常运行，但持久化审计 KV 尚未绑定。配置完成前只能在 Workers 结构化日志中追踪访问。' : error.message}</p>
        <button className="button button--secondary" onClick={onRetry}>重新检查</button>
      </section>
    );
  }

  return (
    <section className="audit-panel panel" aria-label="GM 操作审计">
      <header className="audit-header">
        <div>
          <div className="audit-header__icon"><FileClock size={19} /></div>
          <div><span className="eyebrow">IMMUTABLE ACCESS TRAIL</span><h2>GM 操作审计</h2></div>
        </div>
        <div className="audit-header__notice"><ShieldAlert size={15} /><span>所有读取和拒绝请求都会留痕；审计读取本身也会进入下一次快照。</span></div>
      </header>

      {items.length === 0 ? (
        <div className="empty-state audit-empty">
          <ArchiveX size={27} /><strong>暂无持久审计记录</strong><p>存储已连接；新的 GM API 访问将在此显示。</p>
        </div>
      ) : (
        <div className="audit-table" role="table" aria-label="最近审计记录">
          <div className="audit-table__head" role="row">
            <span role="columnheader">时间</span><span role="columnheader">操作者</span><span role="columnheader">动作 / 资源</span><span role="columnheader">结果</span><span role="columnheader">Request ID</span>
          </div>
          <div className="audit-table__body">
            {items.map((event) => (
              <article className="audit-row" role="row" key={`${event.requestId}-${event.occurredAt}`}>
                <div role="cell" data-label="时间" className="audit-row__time">
                  <strong>{formatTime(event.occurredAt)}</strong>
                  <span><Clock3 size={12} />{formatDuration(event.durationMs)}</span>
                </div>
                <div role="cell" data-label="操作者" className="audit-actor">
                  {event.actor ? (
                    <><span className="audit-avatar">{event.actor.email.slice(0, 2).toUpperCase()}</span><div><strong>{event.actor.email}</strong><span>{event.actor.role}</span></div></>
                  ) : (
                    <><span className="audit-avatar audit-avatar--anonymous"><UserRoundX size={14} /></span><div><strong>未认证请求</strong><span>anonymous</span></div></>
                  )}
                </div>
                <div role="cell" data-label="动作 / 资源" className="audit-action"><code>{event.action}</code><span>{event.resource}</span></div>
                <div role="cell" data-label="结果" className="audit-result">
                  <span className={`outcome outcome--${event.outcome}`}>{outcomeLabel[event.outcome]}</span>
                  <code>HTTP {event.status}</code>
                </div>
                <div role="cell" data-label="Request ID" className="audit-request"><code>{event.requestId}</code><span>trace {event.traceId.slice(0, 8)}</span></div>
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
